import React, { useEffect, useState } from "react";
import { Helmet } from 'react-helmet';
import {
  Grid,
  Box,
  Paper,
  TableContainer,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Button,
  Typography,
  Chip,
  alpha,
  Avatar,
  Divider,
  IconButton,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import { useParams, useHistory } from "react-router-dom";
import AlbumIcon from '@mui/icons-material/Album';
import ImageIcon from '@mui/icons-material/Image';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import * as actions from "./actions";
import { API_URL } from "../../constants";
import moment from "moment";
import CustomShowModal from "../Common/customShowModal";
import ViewSong from "./ViewSong";
import ConfirmModal from "../Common/ConfirmModal";
import CustomAddModal from "../Common/CustomAddModal";
import { toastSuccess } from "../../utils";
import SelectCMP from "react-select";

// Styled Components
const GlassCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 16,
  background: alpha('#1E293B', 0.5),
  backdropFilter: 'blur(20px)',
  border: `1px solid ${alpha('#fff', 0.08)}`,
  boxShadow: `0 8px 32px ${alpha('#000', 0.3)}`,
  marginBottom: theme.spacing(3),
}));

const PageHeader = styled(Box)(({ theme}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(4),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.1rem',
  color: '#F1F5F9',
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const InfoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  padding: theme.spacing(1.5, 0),
  borderBottom: `1px solid ${alpha('#fff', 0.06)}`,
  '&:last-child': {
    borderBottom: 'none',
  },
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: '#94A3B8',
  fontSize: '0.875rem',
  minWidth: '180px',
  flexShrink: 0,
}));

const InfoValue = styled(Typography)(({ theme }) => ({
  color: '#E2E8F0',
  fontSize: '0.875rem',
  fontWeight: 500,
  flex: 1,
  wordBreak: 'break-word',
}));

const AlbumArtworkContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  borderRadius: 16,
  overflow: 'hidden',
  backgroundColor: alpha('#0F172A', 0.5),
  border: `1px solid ${alpha('#fff', 0.08)}`,
  aspectRatio: '1/1',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
}));

const StatusBadge = styled(Chip)(({ status }) => {
  const colors = {
    approved: { bg: alpha('#66BB6A', 0.15), color: '#66BB6A', border: alpha('#66BB6A', 0.3) },
    rejected: { bg: alpha('#FF6B6B', 0.15), color: '#FF6B6B', border: alpha('#FF6B6B', 0.3) },
    in_review: { bg: alpha('#FFA726', 0.15), color: '#FFA726', border: alpha('#FFA726', 0.3) },
    takendown: { bg: alpha('#9E9E9E', 0.15), color: '#9E9E9E', border: alpha('#9E9E9E', 0.3) },
  };
  const statusColor = colors[status] || colors.in_review;
  return {
    backgroundColor: statusColor.bg,
    color: statusColor.color,
    border: `1px solid ${statusColor.border}`,
    fontWeight: 600,
    fontSize: '0.875rem',
    height: 32,
    textTransform: 'capitalize',
  };
});

const StyledTable = styled(Table)(({ theme }) => ({
  '& .MuiTableHead-root': {
    '& .MuiTableCell-root': {
      backgroundColor: alpha('#12B4A1', 0.1),
      color: '#12B4A1',
      fontWeight: 700,
      fontSize: '0.875rem',
      borderBottom: `2px solid ${alpha('#12B4A1', 0.3)}`,
      padding: '16px',
    },
  },
  '& .MuiTableBody-root': {
    '& .MuiTableRow-root': {
      transition: 'background-color 0.2s ease',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: alpha('#12B4A1', 0.05),
      },
    },
    '& .MuiTableCell-root': {
      color: '#E2E8F0',
      borderBottom: `1px solid ${alpha('#fff', 0.06)}`,
      padding: '16px',
    },
  },
}));

const UserInfoBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: alpha('#12B4A1', 0.05),
  borderRadius: 12,
  border: `1px solid ${alpha('#12B4A1', 0.1)}`,
}));

function ViewReleases(props) {
  const params = useParams();
  const history = useHistory();

  const [release, setRelease] = useState({});
  const [openSong, setOpenSong] = useState(false);
  const [selectedSong, setSelectedSong] = useState({});
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedId] = useState(null);
  const [openReject, setOpenReject] = useState(false);
  const [reasonText, setReasonText] = useState("");
  const [showSongModal, setShowSongModal] = useState(false);
  const [rejectOptions, setRejectOptions] = useState([]);
  const [recipeFilePreview] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [openTrackDetails, setOpenTrackDetails] = useState(false);
  const [selectedTrackDetails, setSelectedTrackDetails] = useState(null);

  const onGetRejectOptions = React.useCallback(() => {
    actions.rejectReasonsAdmin().then((res) => {
      setRejectOptions(
        Array.isArray(res.body)
          ? res.body.map((item) => ({ label: item.reason, value: item.reason }))
          : []
      );
    });
  }, []);

  const onGetRelease = React.useCallback(() => {
    props.setLoader(true);
    actions
      .getNewReleaseByIdAdmin(params.id)
      .then((res) => {
        props.setLoader(false);
        setRelease(res.body);
        setImageError(false);
      })
      .catch((err) => {
        props.setLoader(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  useEffect(() => {
    onGetRelease();
    onGetRejectOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  const onOpenModal = (item) => {
    setSelectedSong(item);
    setOpenSong(true);
  };

  const onConfirmAcceptModal = () => {
    props.setLoader(true);
    actions
      .changeReleaseStatusAdmin({ status: "approved", release_id: selectedId })
      .then((res) => {
        setOpenConfirm(false);
        props.setLoader(false);
        onGetRelease();
      })
      .catch((err) => {
        props.setLoader(false);
      });
  };

  const onSubmitReject = () => {
    props.setLoader(true);
    actions
      .changeReleaseStatusAdmin({
        release_id: selectedId,
        status: "rejected",
        reason: reasonText,
      })
      .then((res) => {
        setOpenReject(false);
        props.setLoader(false);
        onGetRelease();
        toastSuccess(res.message);
      })
      .catch((err) => {
        props.setLoader(false);
      });
  };

  const onClickAccept = (id) => {
    console.log("onClickAccept triggered");
  };

  const onClickReject = (id) => {
    console.log("onClickReject triggered");
  };

  const onDeleteRejectedRelease = (id) => {
    props.setLoader(true);
    actions
      .deleteRejectedRelease({ release_id: id })
      .then((res) => {
        props.setLoader(false);
        history.push("/releases/review");
      })
      .catch((err) => {
        props.setLoader(false);
      });
  };

  const {
    album_artwork = "",
    apple_id1 = "",
    apple_id1f = "",
    apple_id2 = "",
    apple_id2f = "",
    apple_id3 = "",
    apple_id3f = "",
    audios = [],
    c_line = "",
    crbt = "",
    featuring1 = "",
    featuring2 = "",
    featuring3 = "",
    genre = "",
    lable_name = "",
    number_of_tracks = "",
    p_line = "",
    primary_artist1 = "",
    primary_artist2 = "",
    primary_artist3 = "",
    release_date = "",
    release_id = "",
    release_title = "",
    release_type = "",
    spotify_id1 = "",
    spotify_id1f = "",
    spotify_id2 = "",
    spotify_id2f = "",
    spotify_id3 = "",
    spotify_id3f = "",
    status = "",
    sub_genre = "",
    upc_ean = "",
    name = "",
    email = "",
    video_platforms = "",
    ogn_release_date = "",
    cour_line = "",
    add_info = "",
  } = release;

  const imageUrl = recipeFilePreview || (album_artwork ? `${API_URL}/${album_artwork}` : '');

  return (
    <Box>
      <Helmet>
        <title>{release.release_title ? `${release.release_title} - Fronic Media Labels` : 'View Release - Fronic Media Labels'}</title>
      </Helmet>

      {/* Header with Back Button */}
      <PageHeader>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton
            onClick={() => history.goBack()}
            sx={{
              backgroundColor: alpha('#12B4A1', 0.1),
              color: '#12B4A1',
              '&:hover': { backgroundColor: alpha('#12B4A1', 0.2) },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#F1F5F9', mb: 0.5 }}>
              {release_title || 'Release Details'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#94A3B8' }}>
              Release ID: {release_id || '-'}
            </Typography>
          </Box>
        </Box>
        <StatusBadge label={status || 'Unknown'} status={status} size="medium" />
      </PageHeader>

      <Grid container spacing={3}>
        {/* Left Column - Album Artwork & User Info */}
        <Grid item xs={12} md={4}>
          <GlassCard>
            <AlbumArtworkContainer>
              {imageError || !imageUrl ? (
                <Box textAlign="center">
                  <ImageIcon sx={{ fontSize: 80, color: alpha('#94A3B8', 0.3), mb: 2 }} />
                  <Typography sx={{ color: '#94A3B8', fontSize: '0.875rem' }}>
                    Image not available
                  </Typography>
                </Box>
              ) : (
                <img
                  src={imageUrl}
                  alt={release_title}
                  onError={() => setImageError(true)}
                />
              )}
            </AlbumArtworkContainer>

            {imageUrl && !imageError && (
              <Button
                href={imageUrl}
                download
                target="_blank"
                fullWidth
                startIcon={<DownloadIcon />}
                sx={{
                  mt: 2,
                  color: '#12B4A1',
                  borderColor: alpha('#12B4A1', 0.3),
                  '&:hover': {
                    backgroundColor: alpha('#12B4A1', 0.1),
                    borderColor: '#12B4A1',
                  },
                }}
                variant="outlined"
              >
                Download Artwork
              </Button>
            )}

            <Box mt={3}>
              <SectionTitle>
                <AlbumIcon fontSize="small" />
                User Information
              </SectionTitle>
              <UserInfoBox>
                <Avatar sx={{ bgcolor: alpha('#12B4A1', 0.2), color: '#12B4A1' }}>
                  {name?.charAt(0)?.toUpperCase() || 'U'}
                </Avatar>
                <Box flex={1}>
                  <Typography sx={{ fontWeight: 600, color: '#F1F5F9', fontSize: '0.95rem' }}>
                    {name || 'Unknown'}
                  </Typography>
                  <Typography sx={{ color: '#94A3B8', fontSize: '0.8rem' }}>
                    {email || '-'}
                  </Typography>
                </Box>
              </UserInfoBox>
            </Box>

            {status === "rejected" && (
              <Button
                onClick={() => onDeleteRejectedRelease(release_id)}
                fullWidth
                variant="contained"
                color="error"
                sx={{ mt: 2 }}
              >
                Delete Release
              </Button>
            )}
          </GlassCard>
        </Grid>

        {/* Right Column - Release Information */}
        <Grid item xs={12} md={8}>
          {/* Basic Information */}
          <GlassCard>
            <SectionTitle>Basic Information</SectionTitle>
            <Box>
              <InfoRow>
                <InfoLabel>Release Type</InfoLabel>
                <InfoValue sx={{ textTransform: 'capitalize' }}>{release_type || '-'}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Label</InfoLabel>
                <InfoValue>{lable_name || '-'}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Genre</InfoLabel>
                <InfoValue>{genre || '-'}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Sub Genre</InfoLabel>
                <InfoValue>{sub_genre || '-'}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Number of Tracks</InfoLabel>
                <InfoValue>{number_of_tracks || '-'}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Release Date</InfoLabel>
                <InfoValue>{release_date ? moment(Number(release_date)).format("MMM DD, YYYY") : '-'}</InfoValue>
              </InfoRow>
              {ogn_release_date && (
                <InfoRow>
                  <InfoLabel>Original Release Date</InfoLabel>
                  <InfoValue>{moment(Number(ogn_release_date)).format("MMM DD, YYYY")}</InfoValue>
                </InfoRow>
              )}
              <InfoRow>
                <InfoLabel>UPC/EAN</InfoLabel>
                <InfoValue>{upc_ean || '-'}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>CRBT</InfoLabel>
                <InfoValue sx={{ textTransform: 'capitalize' }}>{crbt || '-'}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Video Platforms</InfoLabel>
                <InfoValue sx={{ textTransform: 'capitalize' }}>{video_platforms || '-'}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>C Line</InfoLabel>
                <InfoValue>{c_line || '-'}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>P Line</InfoLabel>
                <InfoValue>{p_line || '-'}</InfoValue>
              </InfoRow>
              {cour_line && (
                <InfoRow>
                  <InfoLabel>Courier Line</InfoLabel>
                  <InfoValue>{cour_line}</InfoValue>
                </InfoRow>
              )}
              {add_info && (
                <InfoRow>
                  <InfoLabel>Additional Info</InfoLabel>
                  <InfoValue>{add_info}</InfoValue>
                </InfoRow>
              )}
            </Box>
          </GlassCard>

          {/* Primary Artists */}
          {primary_artist1 && (
            <GlassCard>
              <SectionTitle>Primary Artists</SectionTitle>
              <Box>
                <InfoRow>
                  <InfoLabel>Primary Artist 1</InfoLabel>
                  <InfoValue>{primary_artist1}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Apple ID</InfoLabel>
                  <InfoValue>{apple_id1 || '-'}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Spotify ID</InfoLabel>
                  <InfoValue>{spotify_id1 || '-'}</InfoValue>
                </InfoRow>

                {primary_artist2 && (
                  <>
                    <Divider sx={{ my: 2, borderColor: alpha('#fff', 0.06) }} />
                    <InfoRow>
                      <InfoLabel>Primary Artist 2</InfoLabel>
                      <InfoValue>{primary_artist2}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Apple ID</InfoLabel>
                      <InfoValue>{apple_id2 || '-'}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Spotify ID</InfoLabel>
                      <InfoValue>{spotify_id2 || '-'}</InfoValue>
                    </InfoRow>
                  </>
                )}

                {primary_artist3 && (
                  <>
                    <Divider sx={{ my: 2, borderColor: alpha('#fff', 0.06) }} />
                    <InfoRow>
                      <InfoLabel>Primary Artist 3</InfoLabel>
                      <InfoValue>{primary_artist3}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Apple ID</InfoLabel>
                      <InfoValue>{apple_id3 || '-'}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Spotify ID</InfoLabel>
                      <InfoValue>{spotify_id3 || '-'}</InfoValue>
                    </InfoRow>
                  </>
                )}
              </Box>
            </GlassCard>
          )}

          {/* Featuring Artists */}
          {featuring1 && (
            <GlassCard>
              <SectionTitle>Featuring Artists</SectionTitle>
              <Box>
                <InfoRow>
                  <InfoLabel>Featuring Artist 1</InfoLabel>
                  <InfoValue>{featuring1}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Apple ID</InfoLabel>
                  <InfoValue>{apple_id1f || '-'}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Spotify ID</InfoLabel>
                  <InfoValue>{spotify_id1f || '-'}</InfoValue>
                </InfoRow>

                {featuring2 && (
                  <>
                    <Divider sx={{ my: 2, borderColor: alpha('#fff', 0.06) }} />
                    <InfoRow>
                      <InfoLabel>Featuring Artist 2</InfoLabel>
                      <InfoValue>{featuring2}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Apple ID</InfoLabel>
                      <InfoValue>{apple_id2f || '-'}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Spotify ID</InfoLabel>
                      <InfoValue>{spotify_id2f || '-'}</InfoValue>
                    </InfoRow>
                  </>
                )}

                {featuring3 && (
                  <>
                    <Divider sx={{ my: 2, borderColor: alpha('#fff', 0.06) }} />
                    <InfoRow>
                      <InfoLabel>Featuring Artist 3</InfoLabel>
                      <InfoValue>{featuring3}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Apple ID</InfoLabel>
                      <InfoValue>{apple_id3f || '-'}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Spotify ID</InfoLabel>
                      <InfoValue>{spotify_id3f || '-'}</InfoValue>
                    </InfoRow>
                  </>
                )}
              </Box>
            </GlassCard>
          )}
        </Grid>
      </Grid>

      {/* Tracks Table */}
      {audios && audios.length > 0 && (
        <GlassCard>
          <SectionTitle sx={{ mb: 3 }}>
            <PlayArrowIcon fontSize="small" />
            Tracks ({audios.length})
          </SectionTitle>
          <Box sx={{ overflowX: 'auto' }}>
            <StyledTable>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell sx={{ minWidth: 200 }}>Title</TableCell>
                  <TableCell>ISRC</TableCell>
                  <TableCell>Language</TableCell>
                  <TableCell>Explicit</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {audios.map((audio, index) => (
                  <TableRow key={index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 500, color: '#F1F5F9' }}>
                        {audio.title || '-'}
                      </Typography>
                      {audio.version_subtitle && (
                        <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8', mt: 0.5 }}>
                          {audio.version_subtitle}
                        </Typography>
                      )}
                      <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8', mt: 0.5 }}>
                        {audio.track_version || 'Original'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {audio.isrc_yes_no === 'Yes' ? (
                        <Typography sx={{ color: '#E2E8F0', fontSize: '0.875rem' }}>
                          {audio.isrc || '-'}
                        </Typography>
                      ) : (
                        <Chip label="No ISRC" size="small" sx={{ fontSize: '0.7rem', bgcolor: alpha('#94A3B8', 0.15), color: '#94A3B8' }} />
                      )}
                    </TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{audio.track_title_language || '-'}</TableCell>
                    <TableCell>
                      {audio.explicit_version?.toLowerCase() === 'yes' ? (
                        <Chip label="Yes" size="small" color="error" sx={{ fontSize: '0.75rem' }} />
                      ) : (
                        <Chip label="No" size="small" sx={{ fontSize: '0.75rem', bgcolor: alpha('#66BB6A', 0.15), color: '#66BB6A' }} />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setSelectedTrackDetails(audio);
                          setOpenTrackDetails(true);
                        }}
                        sx={{
                          color: '#12B4A1',
                          borderColor: alpha('#12B4A1', 0.3),
                          fontSize: '0.75rem',
                          textTransform: 'none',
                          '&:hover': {
                            backgroundColor: alpha('#12B4A1', 0.1),
                            borderColor: '#12B4A1',
                          },
                        }}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </StyledTable>
          </Box>
        </GlassCard>
      )}

      {/* Modals */}
      <CustomShowModal
        open={openSong}
        handleClose={() => setOpenSong(false)}
        data={selectedSong}
      />

      <ConfirmModal
        open={openConfirm}
        title="Confirm Approval"
        message="Are you sure you want to approve this release?"
        onConfirm={onConfirmAcceptModal}
        onClose={() => setOpenConfirm(false)}
      />

      <CustomAddModal
        open={openReject}
        handleClose={() => setOpenReject(false)}
        title="Reject Release"
      >
        <Box p={2}>
          <Typography sx={{ color: '#E2E8F0', mb: 2 }}>
            Select a reason for rejection:
          </Typography>
          <SelectCMP
            options={rejectOptions}
            onChange={(option) => setReasonText(option.value)}
            placeholder="Select reason..."
            styles={{
              control: (base) => ({
                ...base,
                backgroundColor: alpha('#0F172A', 0.5),
                borderColor: alpha('#fff', 0.1),
                color: '#E2E8F0',
              }),
              menu: (base) => ({
                ...base,
                backgroundColor: '#1E293B',
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isFocused ? alpha('#12B4A1', 0.2) : 'transparent',
                color: '#E2E8F0',
              }),
              singleValue: (base) => ({
                ...base,
                color: '#E2E8F0',
              }),
            }}
          />
          <Box display="flex" gap={2} mt={3}>
            <Button
              variant="outlined"
              onClick={() => setOpenReject(false)}
              fullWidth
              sx={{
                borderColor: alpha('#fff', 0.2),
                color: '#E2E8F0',
                '&:hover': { borderColor: alpha('#fff', 0.3), backgroundColor: alpha('#fff', 0.05) },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={onSubmitReject}
              disabled={!reasonText}
              fullWidth
            >
              Reject Release
            </Button>
          </Box>
        </Box>
      </CustomAddModal>

      {/* Track Details Modal */}
      <CustomAddModal
        open={openTrackDetails}
        handleClose={() => setOpenTrackDetails(false)}
        title={selectedTrackDetails?.title || 'Track Details'}
        maxWidth="md"
      >
        {selectedTrackDetails && (
          <Box p={3} sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {/* Basic Track Info */}
            <Box mb={3}>
              <Typography sx={{ fontWeight: 700, color: '#12B4A1', fontSize: '1rem', mb: 2 }}>
                Basic Information
              </Typography>
              <InfoRow>
                <InfoLabel>Title</InfoLabel>
                <InfoValue>{selectedTrackDetails.title || '-'}</InfoValue>
              </InfoRow>
              {selectedTrackDetails.version_subtitle && (
                <InfoRow>
                  <InfoLabel>Version Subtitle</InfoLabel>
                  <InfoValue>{selectedTrackDetails.version_subtitle}</InfoValue>
                </InfoRow>
              )}
              <InfoRow>
                <InfoLabel>Track Version</InfoLabel>
                <InfoValue sx={{ textTransform: 'capitalize' }}>{selectedTrackDetails.track_version || '-'}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Instrumental</InfoLabel>
                <InfoValue sx={{ textTransform: 'capitalize' }}>{selectedTrackDetails.instrumental || '-'}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Producer</InfoLabel>
                <InfoValue>{selectedTrackDetails.producer || '-'}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Genre</InfoLabel>
                <InfoValue>{selectedTrackDetails.genre || '-'}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Sub Genre</InfoLabel>
                <InfoValue>{selectedTrackDetails.subgenre || '-'}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Price Tier</InfoLabel>
                <InfoValue>{selectedTrackDetails.price_tier || '-'}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Explicit Version</InfoLabel>
                <InfoValue>
                  {selectedTrackDetails.explicit_version?.toLowerCase() === 'yes' ? (
                    <Chip label="Yes" size="small" color="error" sx={{ fontSize: '0.75rem' }} />
                  ) : (
                    <Chip label="No" size="small" sx={{ fontSize: '0.75rem', bgcolor: alpha('#66BB6A', 0.15), color: '#66BB6A' }} />
                  )}
                </InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Track Language</InfoLabel>
                <InfoValue sx={{ textTransform: 'capitalize' }}>{selectedTrackDetails.track_title_language || '-'}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Lyrics Language</InfoLabel>
                <InfoValue sx={{ textTransform: 'capitalize' }}>{selectedTrackDetails.lyrics_language || '-'}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Caller Tune</InfoLabel>
                <InfoValue>{selectedTrackDetails.caller_tune || '-'}</InfoValue>
              </InfoRow>
            </Box>

            <Divider sx={{ my: 3, borderColor: alpha('#fff', 0.1) }} />

            {/* ISRC Info */}
            <Box mb={3}>
              <Typography sx={{ fontWeight: 700, color: '#12B4A1', fontSize: '1rem', mb: 2 }}>
                ISRC & Codes
              </Typography>
              <InfoRow>
                <InfoLabel>ISRC Available</InfoLabel>
                <InfoValue sx={{ textTransform: 'capitalize' }}>{selectedTrackDetails.isrc_yes_no || '-'}</InfoValue>
              </InfoRow>
              {selectedTrackDetails.isrc_yes_no === 'Yes' && (
                <InfoRow>
                  <InfoLabel>ISRC Code</InfoLabel>
                  <InfoValue>{selectedTrackDetails.isrc || '-'}</InfoValue>
                </InfoRow>
              )}
              {selectedTrackDetails.video_isrc && (
                <InfoRow>
                  <InfoLabel>Video ISRC</InfoLabel>
                  <InfoValue>{selectedTrackDetails.video_isrc}</InfoValue>
                </InfoRow>
              )}
            </Box>

            <Divider sx={{ my: 3, borderColor: alpha('#fff', 0.1) }} />

            {/* Primary Artists */}
            {selectedTrackDetails.primary_artist1 && (
              <>
                <Box mb={3}>
                  <Typography sx={{ fontWeight: 700, color: '#12B4A1', fontSize: '1rem', mb: 2 }}>
                    Primary Artists
                  </Typography>
                  <InfoRow>
                    <InfoLabel>Primary Artist 1</InfoLabel>
                    <InfoValue>{selectedTrackDetails.primary_artist1}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Apple ID</InfoLabel>
                    <InfoValue>{selectedTrackDetails.apple_id1 || '-'}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Spotify ID</InfoLabel>
                    <InfoValue>{selectedTrackDetails.spotify_id1 || '-'}</InfoValue>
                  </InfoRow>
                  {selectedTrackDetails.primary_artist2 && (
                    <>
                      <Box my={2} />
                      <InfoRow>
                        <InfoLabel>Primary Artist 2</InfoLabel>
                        <InfoValue>{selectedTrackDetails.primary_artist2}</InfoValue>
                      </InfoRow>
                      <InfoRow>
                        <InfoLabel>Apple ID</InfoLabel>
                        <InfoValue>{selectedTrackDetails.apple_id2 || '-'}</InfoValue>
                      </InfoRow>
                      <InfoRow>
                        <InfoLabel>Spotify ID</InfoLabel>
                        <InfoValue>{selectedTrackDetails.spotify_id2 || '-'}</InfoValue>
                      </InfoRow>
                    </>
                  )}
                  {selectedTrackDetails.primary_artist3 && (
                    <>
                      <Box my={2} />
                      <InfoRow>
                        <InfoLabel>Primary Artist 3</InfoLabel>
                        <InfoValue>{selectedTrackDetails.primary_artist3}</InfoValue>
                      </InfoRow>
                      <InfoRow>
                        <InfoLabel>Apple ID</InfoLabel>
                        <InfoValue>{selectedTrackDetails.apple_id3 || '-'}</InfoValue>
                      </InfoRow>
                      <InfoRow>
                        <InfoLabel>Spotify ID</InfoLabel>
                        <InfoValue>{selectedTrackDetails.spotify_id3 || '-'}</InfoValue>
                      </InfoRow>
                    </>
                  )}
                </Box>
                <Divider sx={{ my: 3, borderColor: alpha('#fff', 0.1) }} />
              </>
            )}

            {/* Featuring Artists */}
            {selectedTrackDetails.featuring1 && (
              <>
                <Box mb={3}>
                  <Typography sx={{ fontWeight: 700, color: '#12B4A1', fontSize: '1rem', mb: 2 }}>
                    Featuring Artists
                  </Typography>
                  <InfoRow>
                    <InfoLabel>Featuring Artist 1</InfoLabel>
                    <InfoValue>{selectedTrackDetails.featuring1}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Apple ID</InfoLabel>
                    <InfoValue>{selectedTrackDetails.apple_id1f || '-'}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Spotify ID</InfoLabel>
                    <InfoValue>{selectedTrackDetails.spotify_id1f || '-'}</InfoValue>
                  </InfoRow>
                  {selectedTrackDetails.featuring2 && (
                    <>
                      <Box my={2} />
                      <InfoRow>
                        <InfoLabel>Featuring Artist 2</InfoLabel>
                        <InfoValue>{selectedTrackDetails.featuring2}</InfoValue>
                      </InfoRow>
                      <InfoRow>
                        <InfoLabel>Apple ID</InfoLabel>
                        <InfoValue>{selectedTrackDetails.apple_id2f || '-'}</InfoValue>
                      </InfoRow>
                      <InfoRow>
                        <InfoLabel>Spotify ID</InfoLabel>
                        <InfoValue>{selectedTrackDetails.spotify_id2f || '-'}</InfoValue>
                      </InfoRow>
                    </>
                  )}
                  {selectedTrackDetails.featuring3 && (
                    <>
                      <Box my={2} />
                      <InfoRow>
                        <InfoLabel>Featuring Artist 3</InfoLabel>
                        <InfoValue>{selectedTrackDetails.featuring3}</InfoValue>
                      </InfoRow>
                      <InfoRow>
                        <InfoLabel>Apple ID</InfoLabel>
                        <InfoValue>{selectedTrackDetails.apple_id3f || '-'}</InfoValue>
                      </InfoRow>
                      <InfoRow>
                        <InfoLabel>Spotify ID</InfoLabel>
                        <InfoValue>{selectedTrackDetails.spotify_id3f || '-'}</InfoValue>
                      </InfoRow>
                    </>
                  )}
                </Box>
                <Divider sx={{ my: 3, borderColor: alpha('#fff', 0.1) }} />
              </>
            )}

            {/* Authors & Composers */}
            <Box mb={3}>
              <Typography sx={{ fontWeight: 700, color: '#12B4A1', fontSize: '1rem', mb: 2 }}>
                Authors & Composers
              </Typography>
              {(() => {
                const authors = [
                  selectedTrackDetails.author1?.replace(/\|~\|/g, ' ') || '',
                  selectedTrackDetails.author2?.replace(/\|~\|/g, ' ') || '',
                  selectedTrackDetails.author3?.replace(/\|~\|/g, ' ') || ''
                ].filter(Boolean);

                const composers = [
                  selectedTrackDetails.composer1?.replace(/\|~\|/g, ' ') || '',
                  selectedTrackDetails.composer2?.replace(/\|~\|/g, ' ') || '',
                  selectedTrackDetails.composer3?.replace(/\|~\|/g, ' ') || ''
                ].filter(Boolean);

                return (
                  <>
                    {authors.length > 0 && (
                      <InfoRow>
                        <InfoLabel>Author(s)</InfoLabel>
                        <InfoValue>{authors.join(', ')}</InfoValue>
                      </InfoRow>
                    )}
                    {composers.length > 0 && (
                      <InfoRow>
                        <InfoLabel>Composer(s)</InfoLabel>
                        <InfoValue>{composers.join(', ')}</InfoValue>
                      </InfoRow>
                    )}
                  </>
                );
              })()}
              {selectedTrackDetails.publisher && (
                <InfoRow>
                  <InfoLabel>Publisher</InfoLabel>
                  <InfoValue>{selectedTrackDetails.publisher}</InfoValue>
                </InfoRow>
              )}
              <InfoRow>
                <InfoLabel>Production Year</InfoLabel>
                <InfoValue>{selectedTrackDetails.production_year || '-'}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>P Line</InfoLabel>
                <InfoValue>{selectedTrackDetails.p_line || '-'}</InfoValue>
              </InfoRow>
            </Box>

            <Divider sx={{ my: 3, borderColor: alpha('#fff', 0.1) }} />

            {/* Distribution & Media */}
            <Box mb={3}>
              <Typography sx={{ fontWeight: 700, color: '#12B4A1', fontSize: '1rem', mb: 2 }}>
                Distribution & Media
              </Typography>
              <InfoRow>
                <InfoLabel>Distribute Music</InfoLabel>
                <InfoValue sx={{ textTransform: 'capitalize' }}>{selectedTrackDetails.destribute_music || '-'}</InfoValue>
              </InfoRow>
              {selectedTrackDetails.download_video_link && (
                <InfoRow>
                  <InfoLabel>Video Link</InfoLabel>
                  <InfoValue>
                    <a
                      href={selectedTrackDetails.download_video_link}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: '#12B4A1', textDecoration: 'none' }}
                    >
                      View Video
                    </a>
                  </InfoValue>
                </InfoRow>
              )}
            </Box>

            {/* Lyrics */}
            {selectedTrackDetails.lyrics && (
              <>
                <Divider sx={{ my: 3, borderColor: alpha('#fff', 0.1) }} />
                <Box>
                  <Typography sx={{ fontWeight: 700, color: '#12B4A1', fontSize: '1rem', mb: 2 }}>
                    Lyrics
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: alpha('#0F172A', 0.5),
                      border: `1px solid ${alpha('#fff', 0.1)}`,
                      borderRadius: 2,
                      padding: 2,
                      maxHeight: 300,
                      overflowY: 'auto',
                    }}
                  >
                    <Typography
                      sx={{
                        whiteSpace: 'pre-wrap',
                        fontSize: '0.875rem',
                        color: '#E2E8F0',
                        fontFamily: 'monospace',
                        lineHeight: 1.6,
                      }}
                    >
                      {selectedTrackDetails.lyrics}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}

            {/* Close Button */}
            <Box mt={4}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setOpenTrackDetails(false)}
                sx={{
                  borderColor: alpha('#fff', 0.2),
                  color: '#E2E8F0',
                  '&:hover': {
                    borderColor: alpha('#fff', 0.3),
                    backgroundColor: alpha('#fff', 0.05),
                  },
                }}
              >
                Close
              </Button>
            </Box>
          </Box>
        )}
      </CustomAddModal>
    </Box>
  );
}

export default ViewReleases;
